/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useState, useEffect } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Link } from '@ta-interaktiv/contentbricks'
import { SectionHead } from 'components/SectionHead'
import { GetPos } from 'components/getPos'
import mapboxgl, { MapLayerMouseEvent, MapMouseEvent } from 'mapbox-gl'
import { HazardLayerSwitch } from 'components/HazardLayerSwitch'
import Scrollchor from 'react-scrollchor'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'
import {
  MapContext,
  MapboxMap,
  Terrain,
  AttributionControl,
  ScaleControl,
  GeolocateControl,
  NavigationControls,
  Source,
  Layer,
  ActivationButton,
  PitchControl,
  ViewportDisplay,
  Filter,
  Marker
} from 'components/MapboxMap'
// TODO Set the correct article ID here
const ARTICLE_ID = '416510118894'
const isMobile = window.innerWidth < 768
const layers = [
  { name: 'Pour les frileux', id: 'cat-frileux', color: '#c51b8a' },
  { name: 'Pour les puristes', id: 'cat-puristes', color: '#c51b8a' },
  { name: 'En train', id: 'cat-ecolos', color: '#c51b8a' },
  { name: 'Tout le territoire', id: 'cat-base', color: '#c51b8a' }
]

export function AppComponent() {
  const [latlng, setLatlng] = useState([0, 0])
  const [map, registerMap] = useState(undefined)

  useEffect(() => {
    console.log(latlng)

    if (map) {
      map.flyTo({
        center: latlng,
        zoom: 10
      })

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'Interactions',
        event_action: 'click',
        event_label: 'showMyPosition'
      })
    }
  }, [latlng])

  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>
              Trouvez les meilleurs endroits de Suisse pour voir les étoiles
            </h1>
            <h2>
              Altitude, visibilité du ciel, pollution lumineuse: nous avons créé
              une carte pour observer au mieux les phénomènes astronomiques
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical very fitted segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>Mathieu Rudaz, Fanny Giroud</div>
            <div className='ta updated'>Mis à jour le 23 août 2021</div>
          </div>
          <div className='ta text'>
            <p>
              Retrouver l’obscurité de la nuit pour faire revenir les étoiles.
              C’est dans ce but que nous avons produit une carte interactive,
              vous permettant de situer les meilleurs endroits pour admirer les
              paysages nocturnes. Avec la pollution lumineuse, les nébuleuses et
              les galaxies sont en effet de plus en plus voilées.
            </p>
            <p>
              Nous avons cherché les endroits les mieux placés selon différents
              facteurs: l’altitude, la pollution lumineuse et la visibilité du
              ciel. Nous avons aussi pris en compte certains paramètres pour
              personnaliser votre consultation de la carte (ne pas monter trop
              haut en altitude, rester proche des transports en commun, ou au
              contraire aller plus au coeur de la nuit).
            </p>
          </div>
          <div className='ta hint big'>
            <b>Avertissement</b>: ces renseignements sont purement indicatifs.
            La méthode de calcul utilisée pour déterminer les zones les plus
            propices à l'observation du ciel nocturne ne tient pas compte des
            bâtiments. Nos données d'émissions lumineuses{' '}
            <b>
              ne tiennent pas compte de l'extinction de l'éclairage publique
            </b>{' '}
            dans certaines communes.
          </div>
        </div>
      </div>

      {/* <div className='wind-turbine-container'>
        <WindTurbine stage={stage} />
        <div className='stages-container'>
          <Waypoint
            onEnter={() => {
              setStage(1)
            }}
          >
            <div className='ui text container'>
              <div className='scroll-text'>
                Donec pede justo, fringilla vel, aliquet nec, vulputate eget,
                arcu. In enim justo, rhoncus ut, imperdiet a, venenatis vitae,
                justo.
              </div>
            </div>
          </Waypoint>
        </div>
      </div> */}

      {map && (
        <div className='ui very fitted vertical segment'>
          <div className='ui text container'>
            <SectionHead>Légende</SectionHead>
            <div className='map-legend'>
              <div className='legend-left'>
                <div className='legend-title'>
                  <b>Pollution lumineuse:</b> radiance (10<sup>10</sup>)
                </div>
                <div className='legend-bars-container visibility'>
                  <div className='legend-bars-labels'>
                    <div className='left'>Faible: 0</div>
                    <div className='right'>Élevée: 500</div>
                  </div>
                  <div className='legend-bars-wrapper'>
                    <div className='legend-box'>
                      <div className='legend-bar' />
                    </div>
                    <div className='legend-box'>
                      <div className='legend-bar' />
                    </div>
                    <div className='legend-box'>
                      <div className='legend-bar' />
                    </div>
                    <div className='legend-box'>
                      <div className='legend-bar' />
                    </div>
                    <div className='legend-box'>
                      <div className='legend-bar' />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='ta hint big'>
              <p>
                Nous avons pris en compte la lumière reçue par les satellites (
                <b>radiance</b>). Plus celle-ci est faible, moins le ciel est
                affecté par la pollution lumineuse. Toutefois, la seule mesure
                de la radiance ne donne qu'un aperçu du réel impact de
                l'éclairage artificiel.
              </p>
              {/* <p>
                La <b>pollution lumineuse</b> se manifeste de manière directe ou
                diffuse. Ainsi, la réflexion de la lumière sur les surfaces, les
                aérosols ou les molécules en suspension dans l’atmosphère
                produit un halo lumineux qui voile le ciel nocturne.
              </p> */}
              <p>
                <b>
                  À titre d'exemple, au coeur de Lausanne ou Genève, la radiance
                  est maximale (500).
                </b>
              </p>
            </div>
          </div>
        </div>
      )}

      {map && (
        <div
          style={{ position: 'relative', zIndex: 1 }}
          className='ui vertical very fitted segment'
        >
          <div className='ui container'>
            <SectionHead>Trouver une adresse ou un lieu</SectionHead>
          </div>
          <div className='ui text container'>
            <GetPos
              handleResult={res => {
                setLatlng(res)
              }}
            />
          </div>
        </div>
      )}

      <div
        style={{ position: 'relative', zIndex: 0 }}
        className={'mapContainer ' + (map ? (map.active ? 'active' : '') : '')}
      >
        <MapboxMap
          getMap={(map: mapboxgl.Map) => registerMap(map)}
          token='pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJjaXNhNGFsdHAwMDB2Mm9wNWdlMTlqMTNuIn0.uj5aGvF3yvEM4V6kNdlbVg'
          initOptions={{
            style: 'mapbox://styles/ta-interaktiv/cks3jhvua7ykb18qggwd42s81',
            // style: 'mapbox://styles/ta-interaktiv/cks4l6x457ny118ligg03l3qw',
            zoom: isMobile ? 6.75 : 7,
            maxZoom: 17,
            minZoom: isMobile ? 3 : 7,
            center: isMobile ? [7, 46.6] : [8.23, 46.6],
            // maxBounds: [
            //   [0, 44],
            //   [18, 49]
            // ],
            attributionControl: false
          }}
        >
          <>
            <AttributionControl
              options={{ customAttribution: 'Source des géodonnées' }}
            />
            <ScaleControl position='bottom-left' />
            <NavigationControls />
            <PitchControl />
            <GeolocateControl />
            <ActivationButton
              locales={{
                activate: 'Naviguer dans la carte',
                deactivate: 'Quitter la navigation'
              }}
            />
            {layers && (
              <HazardLayerSwitch
                isMobile={false}
                hazards={layers.map(m => {
                  return m
                })}
              />
            )}
          </>
        </MapboxMap>
      </div>

      <div className='ui vertical segment map-continue'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4 className='title'>En savoir plus sur la carte</h4>
            <p>
              Pour réaliser cette carte, nous avons compilé les données
              disponibles les plus récentes des émissions lumineuses de l'Office
              fédéral de l'environnement et du modèle numérique du terrain de
              l'Office fédéral de la topographie.
            </p>
            <p>
              Nous avons croisé trois paramètres (les émissions lumineuses,
              l'altitude et la visibilité du ciel) afin de déterminer les
              endroits les plus propices à l'observation du ciel nocturne pour
              chacun des trois scénarios suivants:
            </p>
            <div className='legend-bloc'>
              <div className='icon-wrapper'>
                <img src={require('../../images/icons_parcs_colored.svg')} />
              </div>
              <p>
                <b>Pour les frileux</b> Si vous voulez observer le ciel sans
                vous exposer trop au froid. Nous avons trouvé les zones situées
                à moins de 500m d'altitude, offrant un minimum de 90% de
                visibilité du ciel et une pollution lumineuse faible à modérée.
                En contrepartie, vous devrez sacrifier un peu de visibilité.{' '}
                <span className='scrollchor'>
                  <Scrollchor to='map'>Vers la carte →</Scrollchor>
                </span>
              </p>
            </div>

            <div className='legend-bloc'>
              <div className='icon-wrapper'>
                <img
                  src={require('../../images/icons_windatlas_colored.svg')}
                />
              </div>
              <p>
                <b>Pour les puristes</b> Si vous n'êtes prêts à aucune
                concession dans votre quête du ciel parfait, nous avons compilé
                les endroits les moins affectés par la pollution lumineuse avec
                une ouverture maximale sur la voûte céleste.
              </p>
              <p>
                Nous ne tenons pas compte de l'altitude dans nos paramètres, ni
                de la proximité de routes ou transports en commun. Alors
                préparez vos chaussures de marche.{' '}
                <span className='scrollchor'>
                  <Scrollchor to='map'>Vers la carte →</Scrollchor>
                </span>
              </p>
            </div>

            <div className='legend-bloc'>
              <div className='icon-wrapper'>
                <img
                  src={require('../../images/icons_visibility_colored.svg')}
                />
              </div>
              <p>
                <b>En train</b> Pour favoriser les transports en commun, nous
                avons sélectionné les lieux éloignés de moins de 2km d'une gare
                suisse. Ils devraient vous permettre d'observer un ciel sombre
                et dégagé, pour autant que vous acceptiez de marcher un peu une
                fois arrivés à destination.
              </p>
              <p>
                Attention au dernier train. Ces renseignements sont purement
                indicatifs, renseignez-vous avant de partir.{' '}
                <span className='scrollchor'>
                  <Scrollchor to='map'>Vers la carte →</Scrollchor>
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4 className='title'>Selon les calculs</h4>
            <p>
              D'après notre analyse, les meilleurs spots d’observation se
              trouvent évidemment en altitude: dans les Alpes, mais aussi dans
              le Jura, par exemple près du col du Marchairuz, sous la cabane du
              Grand Cunay ou à la Roche Champion, tout proche de la frontière
              française.
            </p>
            <p>
              Si l’on ne souhaite pas s’aventurer trop haut, le Nord vaudois
              permet de s’isoler un peu des sources lumineuses, notamment entre
              Onnens et Vaumarcus, ou entre Cheseaux-Noréaz et Yvonand.
              Attention toutefois aux zones inondées du bord du lac de
              Neuchâtel.
            </p>
            <p>
              Pour ceux qui prévoient une expédition avec un retour en train,
              notre carte propose de poser sa couverture près de Ballens, sur la
              ligne du MBC, ou entre St-Cergue et La Cure, avec le NSC.
            </p>
            <p>
              Dans le canton de Genève, les conditions de luminosité ne sont pas
              idéales, mais elles seront un peu meilleures du côté de Chancy,
              tout à l’ouest du canton, ou au bord du lac à Hermance.
            </p>
            <p>
              Un peu plus loin à l’Est, dans le triangle entre Fribourg, Berne
              et Thoune, le Parc naturel du Gantrisch offre de très bonnes
              conditions d’observation. Il compte d’ailleurs quatre plateformes
              d’observation des étoiles.
            </p>
          </div>
        </div>
      </div>

      {/* <div className='ui vertical fitted segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4 className='title'>Les communes qui débranchent</h4>

            <p>
              Cette année, une centaine de{' '}
              <Link href='880173002664'>communes romandes</Link> - dont une
              grande partie du Nord vaudois - a accepté d’éteindre l’éclairage
              public pendant la nuit du 12 au 13 août, à l’initiative de
              l’association{' '}
              <Link href='http://www.projet-perseides.org/'>
                Projet Perséides
              </Link>
              . Depuis 2019, celle-ci invite les communes à se plonger dans
              l’obscurité pour observer la voûte céleste. Avec Orbe comme fer de
              lance en 2019, plusieurs villes ont rejoint le mouvement depuis,
              notamment Yverdon, Villeneuve, Pully, Chavornay, Vallorbe, Moudon,
              Yvonand, une grande partie de l’ouest lausannois, Bulle,
              Neuchâtel, La Chaux-de-Fonds et Le Locle.
            </p>
            <p>
              Lausanne n’éteindra pas ses lumières à l'exception de quelques
              bâtiments emblématiques, regrette Nicolas Genoud, du Projet
              Perséides: «La radiance des villes illumine fortement le reste du
              territoire. À Orbe, un tiers du ciel est illuminé par Lausanne.»
              Au Suchet, le smog lumineux est tel qu’on ne voit pas les étoiles
              filantes, rapporte Gloria Vallone, astronome amateure du projet
              Perséides.
            </p>
            <AutofitIFrame
              loading='lazy'
              aria-label='Interactive line chart'
              src='https://datawrapper.dwcdn.net/MO4eg/'
            />
          </div>
        </div>
      </div> */}

      <div className='ui vertical grey-special segment' id='methode'>
        <div
          className='ui ta text container methodik'
          style={{ position: 'relative' }}
        >
          <div className='innerWrapperMethodik'>
            <h4>Méthodologie</h4>

            <p>
              Les données sur l'obscurité nocturne nous ont été fournies par{' '}
              <Link href='https://www.wsl.ch/en/index.html'>
                L'Institut fédéral
              </Link>{' '}
              de recherches sur la forêt, la neige et le paysage. Les émissions
              lumineuses indiquent la quantité de lumière émise par la Terre
              dans l'espace durant la nuit. Celles-ci sont basées sur des
              observations d'images satellites. La luminosité de la nuit est
              calculée en moyenne pour une année civile. Les données sont
              complétées en atténuant les pics annuels et les imprécisions du
              géoréférencement par satellite.
              <br />
              Vous trouverez davantage d'informations sur l'acquisition et le
              traitement des données ainsi que l'impact des émissions lumineuses
              sur les forêts suisses dans{' '}
              <Link href='https://interactif.24heures.ch/2021/perseides/Kienast-2019-Wie_Lichtemissionen_den_Schweizer_Wald-published_version.pdf'>
                cet article
              </Link>
              .
            </p>
            <p>
              Le modèle numérique du terrain, utilisé pour calculer la
              visibilité du ciel et l'altitude, provient de l'Office fédéral de
              la topographie{' '}
              <Link href='https://www.swisstopo.admin.ch/fr/geodata/height/dhm25200.html'>
                swisstopo
              </Link>
              . Celui-ci ne prend en compte que la surface du pays, sans la
              végétation et les constructions.
            </p>
            <p>
              La visibilité du ciel a été calculée grâce au module SAGA{' '}
              <Link href='http://www.saga-gis.org/saga_tool_doc/2.2.0/ta_lighting_3.html'>
                Sky View Factor
              </Link>
              . Le relief environnant sert de base de calcul. Si vous vous
              trouvez à flanc de montagne, par exemple, la visibilité du ciel
              sera faible. Au contraire, dans une plaine, votre champ de vision
              s'élargit. Le résultat a ensuite été affiné en éliminant les{' '}
              <Link href='https://www.swisstopo.admin.ch/fr/geodata/landscape/tlm3d.html'>
                zones
              </Link>{' '}
              couvertes par des forêts.
            </p>
            <p>
              <b>Paramètres des frileux</b>
            </p>
            <ul className='map-parameters'>
              <li>Radiance: {'<200'}</li>
              <li>Ciel visible: {'>75%'}</li>
              <li>Altitude: {'<500m'}</li>
            </ul>
            <p>
              <b>Paramètres des puristes</b>
            </p>

            <ul className='map-parameters'>
              <li>Radiance: {'<50'}</li>
              <li>Ciel visible: {'>90%'}</li>
            </ul>
            <p>
              <b>Paramètres «en train»</b>
            </p>

            <ul className='map-parameters'>
              <li>Radiance: {'<100'}</li>
              <li>Ciel visible: {'>85%'}</li>
              <li>Altitude: {'<2000m'}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* <div className='ui vertical segment'>
        <div className='ui container'>
          <h4 className='ui header'>Plus d'articles sur ce thème</h4>
          <ArticleTeasers
            numberOfCards='three'
            articleIds={['296923473800', '144103221220', '318900955135']}
          />
        </div>
      </div> */}

      <Footer
        mailTo='mathieu.rudaz@tamedia.ch'
        locale='fr'
        hidePortfolioTeaser
        articleId={ARTICLE_ID}
        credits={[
          ['Textes', 'Fanny Giroud, Mathieu Rudaz'],
          ['Conception et réalisation', 'Mathieu Rudaz']
        ]}
      />
    </div>
  )
}
